/* global runtime */
// Manuelle posteringer, faktura-PDF/utsending, bilag og betalinger/kreditering.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.

const accountCodeFromLabel = (value, fallback = '') => {
  const raw = String(value || '').trim();
  const reskontro = raw.match(/^(1500|2400):/);
  if (reskontro) return reskontro[1];
  const leading = raw.match(/^([0-9][0-9A-Za-z:._-]*)/)?.[1];
  if (leading) return leading;
  const match = raw.match(/\b(\d{4})\b/);
  return match ? match[1] : fallback;
};
const manualPostingCounterpartyFields = (line, side) => {
  const party = line?.[side === 'from' ? 'fraMotpart' : 'tilMotpart'];
  if (!party || typeof party !== 'object') return {};
  const type = String(party.type || '').trim();
  if (!['customer', 'supplier'].includes(type)) return {};
  return stripUndefinedDeep({
    [`${side}CounterpartyType`]: type,
    [`${side}CounterpartyId`]: String(party.id || '').trim(),
    [`${side}CounterpartyName`]: String(party.name || '').trim(),
    [`${side}CounterpartyNumber`]: String(party.number || '').trim(),
  });
};

const buildManualPostingEntries = async (postings = []) => {
  const country = currentCountry();
  const validPostings = safeArray(postings).filter((posting) => safeArray(posting.lines).length);
  if (!validPostings.length) throw new Error(t('svc.addEntry'));
  const saved = [];
  for (const posting of validPostings) {
    const debitSum = safeArray(posting.lines).reduce((sum, line) => sum + num(line.tilBelop), 0);
    const creditSum = safeArray(posting.lines).reduce((sum, line) => sum + num(line.fraBelop), 0);
    if (!debitSum || Math.abs(debitSum - creditSum) > 0.005) {
      throw new Error(t('svc.manualEntryMustBalance'));
    }
    safeArray(posting.lines).forEach((line) => {
      const fromAccountCode = window.PlummeManualPostingRules.accountCode(line.fraKonto);
      const toAccountCode = window.PlummeManualPostingRules.accountCode(line.tilKonto);
      if (!fromAccountCode || !toAccountCode) {
        throw new Error(t('bam.inv.pickFromTo'));
      }
      if (window.PlummeManualPostingRules.sameAccount(line.fraKonto, line.tilKonto)) {
        throw new Error(t('bam.inv.sameAccount'));
      }
      [
        { account: line.fraKonto, vatCode: line.fraMva },
        { account: line.tilKonto, vatCode: line.tilMva },
      ].forEach(({ account, vatCode }) => {
        const vat = normalizeManualVatCode(vatCode, country);
        if (vat.kind === 'none') return;
        if (vat.kind === 'inputOnly') {
          if (window.PlummeManualPostingRules.accountCode(account) !== '2710') throw new Error(t('svc.manualVatCode14Account'));
          return;
        }
        const eligibility = window.PlummeManualPostingRules.vatEligibility(account, {
          country,
          chartAccounts: getChartAccountsModel(),
        });
        if (!eligibility.allowed) throw new Error(t('bam.inv.vatCodeNotAllowed', { code: eligibility.code || account }));
      });
    });
    if (posting.copiedFromPostingId && !posting.date) throw new Error(t('transaction.copyDateRequired'));
    const id = posting.id && !String(posting.id).startsWith('tmp') ? posting.id : `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`; const description = String(posting.desc || posting.description || '').trim(); if (!description) throw new Error(t('bam.inv.describeAllPostings'));
    const normalized = stripUndefinedDeep({
      id,
      date: posting.date || todayIso(),
      description,
      amountNet: debitSum,
      vatRate: 0,
      status: 'active',
      voucherNumber: posting.voucherNumber || undefined,
      source: 'manual_posting', copiedFromPostingId: posting.copiedFromPostingId || undefined, copiedFromVoucherNumber: posting.copiedFromVoucherNumber || undefined,
      supplierPaymentInvoiceId:posting.supplierPaymentInvoiceId||undefined,
      country,
      manualPostingCountry: country,
      manualPosting: true, bankTrace: posting.bankTrace, // Banksporet folger posteringen inn i mutasjonen; klienten kan ikke lagre `expenses` selv.
      directVatAccountAcknowledgement: String(posting.directVatAccountAcknowledgement || '').trim().toUpperCase() || undefined,
      settlementSelections: safeArray(posting.lines).some(line=>line.supplierInvoiceId)?window.PlummeSupplierPaymentFlow.selections(posting):[],
      privatePurchaseId: posting.privatePurchaseId || undefined, supplierCreditForExpenseId: posting.supplierCreditForExpenseId || undefined,
      supplierCreditRefundForId: posting.supplierCreditRefundForId || undefined,
      manualPostingLines: safeArray(posting.lines).map((line) => stripUndefinedDeep({
        id: line.id || `line-${Date.now()}`,
        fromAccount: accountCodeFromLabel(line.fraKonto),
        fromAccountLabel: line.fraKonto || '',
        fromAmount: num(line.fraBelop),
        fromVatCode: line.fraMva || '',
        fromVatRate: normalizeManualVatCode(line.fraMva, country).rate,
        fromVatKind: normalizeManualVatCode(line.fraMva, country).kind,
        fromReportVatCode: normalizeManualVatCode(line.fraMva, country).reportCode,
        ...manualPostingCounterpartyFields(line, 'from'),
        toAccount: accountCodeFromLabel(line.tilKonto),
        toAccountLabel: line.tilKonto || '',
        toAmount: num(line.tilBelop),
        toVatCode: line.tilMva || '',
        toVatRate: normalizeManualVatCode(line.tilMva, country).rate,
        toVatKind: normalizeManualVatCode(line.tilMva, country).kind,
        toReportVatCode: normalizeManualVatCode(line.tilMva, country).reportCode,
        ...manualPostingCounterpartyFields(line, 'to'),
      })),
      createdAt: posting.createdAt || nowIso(),
    });
    const withAttachments = await uploadEditorAttachments(normalized, safeArray(posting.att), 'manual-postings');
    saved.push(withAttachments);
  }
  return saved;
};

const persistManualPostingEntriesLocal = async (saved = []) => {
  await updateStateAndPersist((state) => ({
    ...state,
    expenses: [...saved, ...safeArray(state.expenses).filter((expense) => !saved.some((entry) => entry.id === expense.id))],
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return saved;
};

const saveManualPostingsLocal = async (postings = []) => persistManualPostingEntriesLocal(await buildManualPostingEntries(postings));
const manualPostingVatSignature = (posting) => window.PlummeManualPostingRules.vatSignature(posting);
const manualPostingVatCodeFromRate = (rate, accountCode) => {
  const normalized = Number(rate) || 0;
  if (normalized <= 0) return 'none';
  return `${/^3/.test(String(accountCode || '').trim()) ? 'output' : 'input'}-${normalized}`;
};

const saveManualPostings = async (postings = []) => {
  const entries = await buildManualPostingEntries(postings);
  const recordedEdit = entries.find((entry) => {
    const existing = safeArray(runtime.appState?.expenses).find((expense) => expense.id === entry.id && expense.manualPosting);
    return existing?.accountingRecordings?.[`manualPosting:${existing.id}`];
  });
  if (recordedEdit) throw new Error(t('svc.manualPostedUseCorrection', 'Et bokført bilag må korrigeres med reversering og erstatningsbilag.'));
  entries.forEach((entry)=>{
    const incomplete = window.PlummeBankPeriodControl.incompleteForPosting(runtime.appState, entry)[0]; if(incomplete) throw new Error(t('bank.control.openingSetupMissing', '', { account: incomplete.name || window.PlummeBankPeriodControl.code(incomplete) }));
    const beforeStart = window.PlummeBankPeriodControl.beforeStart(runtime.appState, entry)[0]; if(beforeStart) throw new Error(t('bank.control.postingBeforeStart', '', { account: beforeStart.name || window.PlummeBankPeriodControl.code(beforeStart), date: beforeStart.startBalanceDate }));
    const existing = safeArray(runtime.appState?.expenses).find((expense)=>expense.id === entry.id && expense.manualPosting);
    assertSimpleVatMutationAllowed({
      previousDate: existing?.date,
      nextDate: entry.date,
      vatAffects: !existing || manualPostingVatSignature(existing) !== manualPostingVatSignature(entry),
      vatPeriodAffects: !window.PlummeManualPostingRules.isWithoutVat(entry, currentCountry()) || Boolean(existing && !window.PlummeManualPostingRules.isWithoutVat(existing, currentCountry())),
    });
  });
  try {
    const result = await authorizedPost('manualPostingMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: entries.some(entry=>entry.settlementSelections?.length) ? 'upsert_with_settlement' : 'upsert',
      postings: entries,
    });
    if (result?.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    return result.postings || entries;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    if(entries.some(entry=>entry.settlementSelections?.length)) throw error;
    return persistManualPostingEntriesLocal(entries);
  }
};

const correctManualPostingDateLocal = async (postingId, date) => {
  const nextDate = String(date || '').slice(0, 10);
  const existing = safeArray(runtime.appState?.expenses).find((expense)=>expense.id === postingId && expense.manualPosting);
  if (!existing) throw new Error(t('svc.manualEntryToCancelNotFound'));
  assertSimpleVatMutationAllowed({previousDate: existing.date, nextDate, vatAffects: true});
  const now = nowIso();
  let nextPosting = null;
  await updateStateAndPersist((state) => ({
    ...state,
    expenses: safeArray(state.expenses).map((expense) => {
      if (expense.id !== postingId || !expense.manualPosting) return expense;
      nextPosting = stripUndefinedDeep({ ...expense, date: nextDate, correctedAt: now, correctionReason: 'Dato rettet fra bankavstemming.', updatedAt: now });
      return nextPosting;
    }),
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return nextPosting;
};

const correctManualPostingDate = async (postingOrId, date) => {
  const postingId = typeof postingOrId === 'string' ? postingOrId : postingOrId?.id;
  const nextDate = String(date || '').slice(0, 10);
  if (!postingId) throw new Error(t('svc.missingManualEntryCancel'));
  if (!/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) throw new Error(t('bam.inv.validDate'));
  const recordedPosting = safeArray(runtime.appState?.expenses).find((entry) => entry.id === postingId && entry.manualPosting);
  if (manualPostingRecording(recordedPosting)) {
    return submitPostedManualPostingCorrection(recordedPosting, {
      date: nextDate, description: recordedPosting.description,
      manualPostingLines: safeArray(recordedPosting.manualPostingLines),
      attachments: safeArray(recordedPosting.attachments),
    }, { reason: 'Dato rettet fra bankavstemming.', operationId: stableManualPostingCorrectionOperationId('date', postingId, { date: nextDate }) });
  }
  try {
    const result = await authorizedPost('manualPostingMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'correct_date',
      postingId,
      date: nextDate,
    });
    if (result.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return safeArray(result.postings)[0] || null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return correctManualPostingDateLocal(postingId, nextDate);
  }
};

const correctManualPostingEntry = async (postingOrId, patch = {}) => {
  const postingId = typeof postingOrId === 'string' ? postingOrId : postingOrId?.id;
  if (!postingId) throw new Error(t('svc.missingManualEntryCancel'));
  const recordedPosting = safeArray(runtime.appState?.expenses).find((entry) => entry.id === postingId && entry.manualPosting);
  if (manualPostingRecording(recordedPosting)) {
    const nextDate = String(patch.date || recordedPosting.date || '').slice(0, 10);
    const bankSideMatch = String(patch.bankPostId || '').match(/-manual-bank-(\d+)-(from|to)$/);
    const lineIndex = bankSideMatch ? Number(bankSideMatch[1]) : safeArray(recordedPosting.manualPostingLines).findIndex((line) => /^19[2-9]\d$/.test(String(line.fromAccount || line.toAccount || '')));
    const selectedLine = safeArray(recordedPosting.manualPostingLines)[lineIndex];
    const side = bankSideMatch?.[2] || (/^19[2-9]\d$/.test(String(selectedLine?.fromAccount || '')) ? 'from' : 'to'); const counterSide = side === 'from' ? 'to' : 'from';
    const replacementLines = safeArray(recordedPosting.manualPostingLines).map((line, index) => {
      if (index !== lineIndex) return { ...line };
      const next = { ...line }, nextAmount = patch.amount == null || patch.amount === '' ? null : Math.abs(num(patch.amount));
      if (nextAmount != null && nextAmount > 0) { next[`${side}Amount`] = nextAmount; next[`${counterSide}Amount`] = nextAmount; }
      if (String(patch.accountCode || '').trim()) next[`${counterSide}Account`] = String(patch.accountCode).trim();
      if (patch.vatRate != null && patch.vatRate !== '') next[`${counterSide}VatCode`] = manualPostingVatCodeFromRate(patch.vatRate, patch.accountCode || next[`${counterSide}Account`]);
      return next;
    });
    return submitPostedManualPostingCorrection(recordedPosting, {
      date: nextDate, description: recordedPosting.description, manualPostingLines: replacementLines,
      attachments: safeArray(recordedPosting.attachments),
    }, { reason: patch.correctionReason || 'Rettet fra bankavstemming.', operationId: patch.operationId || stableManualPostingCorrectionOperationId('bank-entry', postingId, {
      date: nextDate, amount: patch.amount, accountCode: patch.accountCode, vatRate: patch.vatRate, bankPostId: patch.bankPostId,
    }) });
  }
  try {
    const result = await authorizedPost('manualPostingMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'correct_entry',
      postingId,
      date: patch.date,
      amount: patch.amount,
      accountCode: patch.accountCode,
      vatRate: patch.vatRate,
      bankPostId: patch.bankPostId,
      receiptInboxEntryId: patch.receiptInboxEntryId,
    });
    if (result.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return safeArray(result.postings)[0] || null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return correctManualPostingEntryLocal(postingId, patch);
  }
};

const getInvoiceDocumentVariant = (invoice) =>
  invoice.kind === 'order_confirmation' ? 'order_confirmation' : 'invoice';

const resolveBackendInvoice = (invoiceOrId) => {
  const invoiceId = typeof invoiceOrId === 'string' ? invoiceOrId : invoiceOrId?.id;
  return safeArray(runtime.appState?.invoices).find((invoice) => invoice.id === invoiceId)
    || (invoiceOrId && typeof invoiceOrId === 'object' ? invoiceOrId._backend : null)
    || (invoiceOrId && typeof invoiceOrId === 'object' ? mapUiInvoiceToBackend(invoiceOrId) : null);
};

const invoicePdfInfo = (invoice) => ({
  storagePath: invoice?.pdfStoragePath || invoice?.pdf?.storagePath || '',
  downloadUrl: invoice?.pdfDownloadUrl || invoice?.pdf?.downloadUrl || '',
  fileName: invoice?.pdfFileName || invoice?.pdf?.fileName || `${invoice?.invoiceNumber || invoice?.id || 'faktura'}.pdf`,
});

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeFilePart(fileName, 'faktura.pdf');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const downloadUrlAsFile = async (url, fileName) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    downloadBlob(await response.blob(), fileName);
  } catch (error) {
    const link = document.createElement('a');
    link.href = url;
    link.download = safeFilePart(fileName, 'faktura.pdf');
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};

const ensureInvoicePdf = async (invoiceOrId, options = {}) => {
  const notifyReady = (result) => (options.onReady?.(result), result);
  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const invoice = resolveBackendInvoice(invoiceOrId);
  if (!invoice?.id) throw new Error(t('svc.missingInvoice'));
  const existingPdf = invoicePdfInfo(invoice);
  if (existingPdf.downloadUrl) return notifyReady({ invoice, ...existingPdf });
  if (existingPdf.storagePath && runtime.storage) {
    const downloadUrl = await runtime.storage.ref().child(existingPdf.storagePath).getDownloadURL().catch(() => '');
    if (downloadUrl) return notifyReady({ invoice, ...existingPdf, downloadUrl });
  }
  const settings = mergeState(runtime.appState).settings;
  const variant = getInvoiceDocumentVariant(invoice);
  const pdf = await getInvoicePdfFromBackend(invoice, settings, variant, { storePdf: true });
  const storedPdf = backendPdfStorageInfo(pdf, invoice);
  const pdfGeneratedAt = nowIso();
  const invoiceWithPdf = stripUndefinedDeep({
    ...invoice,
    pdfStoragePath: storedPdf.storagePath || undefined,
    pdfDownloadUrl: storedPdf.downloadUrl || undefined,
    pdfFileName: storedPdf.fileName,
    pdfMimeType: 'application/pdf',
    pdfGeneratedAt,
    pdf: {
      storagePath: storedPdf.storagePath || undefined,
      downloadUrl: storedPdf.downloadUrl || undefined,
      fileName: storedPdf.fileName,
      mimeType: 'application/pdf',
      generatedAt: pdfGeneratedAt,
    },
  });
  const readyPdf = { invoice: invoiceWithPdf, ...storedPdf, pdfBase64: pdf.pdfBase64 };
  notifyReady(readyPdf); await replaceInvoiceAndPersist(invoiceWithPdf); return readyPdf;
};

// E-posten kunden mottar bygges i backend (`functions/mail/invoiceEmailTemplate.ts`).
// Klienten hadde tidligere sin egen HTML-variant, og da fikk kunden forskjellig
// e-post avhengig av om foretaket eller autopiloten sendte fakturaen.

const findCustomerForOtherSale = (sale, state = mergeState(runtime.appState)) => {
  if (sale?.customer?.email || sale?.customer?.name) return sale.customer;
  const customerId = String(sale?.customerId || '').trim();
  if (!customerId) return sale?.customer || null;
  return safeArray(state.customers).find((customer) => String(customer.id || '') === customerId) || sale?.customer || null;
};

const otherSaleGrossAmount = (sale) => globalThis.PlummeOtherSaleAmounts.totals(sale).gross;

const buildOtherSaleReceiptInvoice = (sale, settings) => {
  const state = mergeState(runtime.appState);
  const customer = findCustomerForOtherSale(sale, state);
  return stripUndefinedDeep({
    id: `other-sale-${sale.id}`,
    otherSaleId: sale.id,
    invoiceNumber: sale.voucherNumber || sale.id,
    kind: 'invoice',
    status: 'completed',
    date: sale.date || todayIso(),
    dueDate: sale.date || todayIso(),
    customer: customer || {},
    customerId: sale.customerId || customer?.id || undefined,
    reference: sale.theirReference || sale.reference || '',
    ourReference: sale.reference || '',
    comment: sale.comment || '',
    attachmentName: safeArray(sale.attachments)[0]?.name || sale.attachmentName || undefined,
    lines: otherSaleReceiptLines(sale),
    settings: {
      locale: uiLocale(),
    },
  });
};

const resolveOtherSaleForDocument = (saleOrId) => {
  const saleId = typeof saleOrId === 'string' ? saleOrId : (saleOrId?.id || saleOrId?.sourceId);
  const existingSale = safeArray(runtime.appState?.otherSales).find((sale) => sale.id === saleId);
  const sourceSale = saleOrId && typeof saleOrId === 'object'
    ? (saleOrId.source || saleOrId._backend || saleOrId)
    : null;
  return stripUndefinedDeep({
    ...(existingSale || {}),
    ...(sourceSale || {}),
  });
};

const otherSaleReceiptPdfInfo = (sale) => ({
  storagePath: sale?.receiptPdfStoragePath || sale?.receiptPdf?.storagePath || '',
  downloadUrl: sale?.receiptPdfDownloadUrl || sale?.receiptPdf?.downloadUrl || '',
  fileName: sale?.receiptPdfFileName || sale?.receiptPdf?.fileName || `${sale?.voucherNumber || sale?.id || 'kvittering'}.pdf`,
});

const downloadOtherSaleReceiptDocument = async (saleOrId) => {
  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const sale = resolveOtherSaleForDocument(saleOrId);
  if (!sale?.id) throw new Error(t('svc.missingSaleToSend'));
  const existingPdf = otherSaleReceiptPdfInfo(sale);
  if (existingPdf.downloadUrl) {
    await downloadUrlAsFile(existingPdf.downloadUrl, existingPdf.fileName);
    return { otherSale: sale, ...existingPdf };
  }
  if (existingPdf.storagePath && runtime.storage) {
    const downloadUrl = await runtime.storage.ref().child(existingPdf.storagePath).getDownloadURL().catch(() => '');
    if (downloadUrl) {
      await downloadUrlAsFile(downloadUrl, existingPdf.fileName);
      return { otherSale: sale, ...existingPdf, downloadUrl };
    }
  }

  const settings = mergeState(runtime.appState).settings;
  const receiptInvoice = buildOtherSaleReceiptInvoice(sale, settings);
  const pdf = await getInvoicePdfFromBackend(receiptInvoice, settings, 'other_sale_receipt', { storePdf: false });
  const fileName = pdf.fileName || existingPdf.fileName || `${sale.voucherNumber || sale.id}.pdf`;
  if (pdf.downloadUrl) {
    await downloadUrlAsFile(pdf.downloadUrl, fileName);
    return { otherSale: sale, downloadUrl: pdf.downloadUrl, fileName };
  }
  if (!pdf.pdfBase64) throw new Error(t('bam.inv.receiptFailed'));
  downloadBlob(pdfBase64ToBlob(pdf.pdfBase64), fileName);
  return { otherSale: sale, pdfBase64: pdf.pdfBase64, fileName };
};

const replaceInvoiceAndPersist = async (nextInvoice) => {
  await upsertInvoice(nextInvoice);
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.drafts));
};

const replaceInvoiceLocal = (nextInvoice) => {
  runtime.appState = mergeState({
    ...mergeState(runtime.appState),
    invoices: [nextInvoice, ...safeArray(runtime.appState?.invoices).filter((entry) => entry.id !== nextInvoice.id)],
  });
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.drafts));
};

const deleteInvoiceLocal = (invoiceId) => {
  runtime.appState = mergeState({
    ...mergeState(runtime.appState),
    invoices: safeArray(runtime.appState?.invoices).filter((entry) => entry.id !== invoiceId),
  });
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.drafts));
};

const shouldFallbackToClientStateWrite = (error) => {
  if (error?.status === 404 && !error?.code) return true;
  if (error?.status || error?.code) return false;
  return /failed to fetch|network ?error|load failed/i.test(error?.message || '');
};

const markSourceCustomerConversationOfferSent = async (invoice, sentAt) => {
  const source = invoice?.sourceCustomerConversation;
  if (!source?.customerId || !source?.threadId || invoice.kind !== 'offer') return null;
  const threads = await loadCustomerConversationThreads(source.customerId);
  const thread = threads.find((entry) => entry.id === source.threadId);
  if (!thread) return null;
  // Gaar gjennom den vanlige lagringen, som skriver ett dokument og bare rorer
  // app-state hvis sammendraget faktisk endrer seg.
  return saveCustomerConversationThread(source.customerId, { ...thread, status: 'offer_sent', updatedAt: sentAt });
};

const sendInvoiceDocument = async (invoice, options = {}) => {
  const onStage = typeof options.onStage === 'function' ? options.onStage : null;
  const deliveryMode = options.deliveryMode === 'link' ? 'link' : 'attachment';
  const emitStage = (key, label, status = 'done', message = '') => {
    onStage?.({ key, label, status, message, at: nowIso() });
  };
  const runStage = async (key, label, action) => {
    emitStage(key, label, 'active');
    try {
      const result = await action();
      emitStage(key, label, 'done');
      return result;
    } catch (error) {
      emitStage(key, label, 'error', error?.message || t('bam.inv.stepFailed'));
      throw error;
    }
  };

  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  if (!invoice?.id) throw new Error(t('svc.missingInvoiceForSending'));

  const settings = mergeState(runtime.appState).settings;
  const existingInvoice = safeArray(runtime.appState?.invoices).find((entry) => entry.id === invoice.id);
  const requestedVariant = options.variant === 'reminder' || options.variant === 'purring' ? options.variant : '';
  const variant = requestedVariant || getInvoiceDocumentVariant(invoice);
  const followUpPaymentSummary = options.followUpPaymentSummary || null;
  const isFollowUpDocument = variant === 'reminder' || variant === 'purring';
  // Adressen brukeren skriver i sendedialogen gjelder denne ene sendingen. Den
  // skal ikke skrives inn i dokumentet foer e-posten faktisk gikk ut: en adresse
  // som ble avvist ble ellers liggende som kundens adresse paa fakturaen, og kom
  // tilbake ferdig utfylt ved neste forsok — ogsaa etter at kundekortet var rettet.
  const requestedRecipientEmail = String(invoice?.customer?.email || '').trim();
  const storedRecipientEmail = String(existingInvoice?.customer?.email || '').trim();
  const baseInvoice = stripUndefinedDeep({
    ...(existingInvoice || {}),
    ...invoice,
    customer: { ...(invoice.customer || {}), email: storedRecipientEmail || undefined },
    status: invoice.status === 'draft' ? 'completed' : (invoice.status || 'completed'),
    // Dokumentet er ikke lenger et utkast. Uten dette blir det gamle
    // utkastflagget liggende, og delingslenken finner ikke fakturaen sin.
    invoiceType: null,
  });

  await runStage('invoice', 'Utkast klargjøres', () => replaceInvoiceAndPersist(baseInvoice));

  // Dokumentet rendres med adressen sendingen faktisk bruker, selv om den ikke
  // er skrevet inn i det lagrede dokumentet enda.
  const invoiceForRender = requestedRecipientEmail
    ? { ...baseInvoice, customer: { ...(baseInvoice.customer || {}), email: requestedRecipientEmail } }
    : baseInvoice;
  const pdf = await runStage('pdf-render', 'Postplumma ankommer', () => getInvoicePdfFromBackend(invoiceForRender, settings, variant, { storePdf: !isFollowUpDocument, followUpPaymentSummary }));
  const storedPdf = backendPdfStorageInfo(pdf, baseInvoice);
  emitStage('pdf-store', 'Dokumentet er klart', 'done');
  const pdfGeneratedAt = nowIso();
  const invoiceWithPdf = isFollowUpDocument ? baseInvoice : stripUndefinedDeep({
    ...baseInvoice,
    pdfStoragePath: storedPdf.storagePath || undefined,
    pdfDownloadUrl: storedPdf.downloadUrl || undefined,
    pdfFileName: storedPdf.fileName,
    pdfMimeType: 'application/pdf',
    pdfGeneratedAt,
    pdf: {
      storagePath: storedPdf.storagePath || undefined,
      downloadUrl: storedPdf.downloadUrl || undefined,
      fileName: storedPdf.fileName,
      mimeType: 'application/pdf',
      generatedAt: pdfGeneratedAt,
    },
  });
  if (!isFollowUpDocument) await replaceInvoiceAndPersist(invoiceWithPdf);
  // Adressen fra sendedialogen lever bare i denne kopien fram til sendingen
  // lykkes. Da skrives den inn i dokumentet sammen med `sentAt`.
  let invoiceForEmail = requestedRecipientEmail
    ? { ...invoiceWithPdf, customer: { ...(invoiceWithPdf.customer || {}), email: requestedRecipientEmail } }
    : invoiceWithPdf;
  let publicLink = '';
  if (deliveryMode === 'link') {
    publicLink = await runStage('public-link', 'Postplumma ankommer', () => getInvoicePublicLink(invoiceWithPdf));
    // Bare lenketokenene skal hentes fra serversvaret. Fakturaen sto tidligere
    // her i sin helhet fra `runtime.appState`, og da forsvant alt som bare fantes
    // i payloaden — foerst og fremst mottakeradressen brukeren skrev i
    // sendedialogen. `invoices` er server-eid state, saa den adressen naar aldri
    // app-state-dokumentet lenkesteget leser fra. Hadde kunden ingen lagret
    // e-post, ble sendingen stille hoppet over uten at det kom noen e-post.
    const linked = safeArray(runtime.appState?.invoices).find((entry) => entry.id === invoiceWithPdf.id);
    if (linked) {
      invoiceForEmail = stripUndefinedDeep({
        ...invoiceForEmail,
        publicViewToken: linked.publicViewToken || invoiceWithPdf.publicViewToken,
        offerAcceptanceToken: linked.offerAcceptanceToken || invoiceWithPdf.offerAcceptanceToken,
        publicLinkGeneratedAt: linked.publicLinkGeneratedAt || invoiceWithPdf.publicLinkGeneratedAt,
      });
    }
  }

  if (!invoiceForEmail.customer?.email || !settings.email) {
    emitStage('email-skipped', 'Dokumentet er klart, men e-postadresse mangler.', 'done');
    return {
      ok: true,
      emailSent: false,
      invoice: invoiceForEmail,
      message: 'Dokumentet er lagret, men e-post ble ikke sendt fordi e-postadresse mangler.',
    };
  }

  const emailResult = await runStage('email', 'Postplumma sender e-posten', () => authorizedPost('sendInvoiceEmailHttp', {
    invoice: invoiceForEmail,
    settings,
    publicLink,
    variant,
    deliveryMode, liveDelivery: options.liveDelivery === true,
    followUpPaymentSummary,
    subject: String(options.emailSubject || '').trim() || undefined,
    // Fritekst brukeren skrev for denne ene sendingen. Den staar i e-posten og
    // skrives ikke inn i dokumentet — en purrekommentar gjelder purringen, ikke
    // fakturaen, og skal ikke dukke opp igjen neste gang dokumentet sendes.
    message: String(options.emailMessage || '').trim() || undefined,
    businessId: runtime.activeBusinessId,
  }));
  const emailMeta = stripUndefinedDeep({
    to: emailResult.to || invoiceForEmail.customer?.email || '',
    fromEmail: emailResult.fromEmail || '',
    fromName: emailResult.fromName || settings.businessName || 'Plumme',
    replyTo: emailResult.replyTo || settings.email || '',
    subject: emailResult.subject || '',
  });

  const sentAt = nowIso();
  const sentInvoice = stripUndefinedDeep({
    ...invoiceForEmail,
    sentAt: invoiceForEmail.sentAt || sentAt,
    sendEvents: [
      ...safeArray(invoiceForEmail.sendEvents),
      {
        id: emailResult.emailId || `send${Date.now()}`,
        sentAt,
        variant,
        deliveryMode,
        source: 'plumme-backend',
        to: emailMeta.to,
        fromEmail: emailMeta.fromEmail,
        fromName: emailMeta.fromName,
        replyTo: emailMeta.replyTo,
      },
    ],
  });
  await replaceInvoiceAndPersist(sentInvoice);
  const updatedCustomer = await markSourceCustomerConversationOfferSent(sentInvoice, sentAt);
  emitStage('sent', 'Suksess! Postplumma er på vei.');
  return {
    ok: true,
    emailSent: true,
    invoice: sentInvoice,
    customer: updatedCustomer || undefined,
    emailId: emailResult.emailId,
    email: emailMeta,
    message: deliveryMode === 'link' ? t('bam.inv.sentAsLink') : t('bam.inv.sentByEmail'),
  };
};

const invoiceCustomerFromBackendCustomer = (customer) => stripUndefinedDeep({
  id: customer.id,
  name: customer.name || 'Uten navn',
  customerNumber: customer.customerNumber || customer.memberNumber || '',
  organizationNumber: customer.organizationNumber || '',
  email: customer.email || '',
  phone: customer.phone || '',
  address: customer.address || '',
  postalCode: customer.postalCode || '',
  postalPlace: customer.postalPlace || '',
  defaultCustomerReference: customer.defaultCustomerReference || '',
  contactPersons: safeArray(customer.contactPersons),
});

// Kundeliste/abonnement: hver kunde har sin egen faktura (opprettes i
// backend-customer-list-invoices.jsx). Her sendes de som har e-post; resten er
// opprettet og maa sendes eller skrives ut manuelt.
const sendInvoiceDocumentToCustomerList = async (invoice, options = {}) => {
  const onStage = typeof options.onStage === 'function' ? options.onStage : null;
  const emitStage = (key, label, status = 'done', message = '') => {
    onStage?.({ key, label, status, message, at: nowIso() });
  };
  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const sourceInvoice = invoice?.customerListBatch ? invoice : resolveBackendInvoice(invoice);
  if (!sourceInvoice?.id) throw new Error(t('svc.missingDraftForSending'));
  if (!customerListInvoiceIds(sourceInvoice, invoice).length && !sourceInvoice.customerListBatch) return sendInvoiceDocument(sourceInvoice, options);

  emitStage('invoice', 'Utkast klargjøres', 'active');
  const batch = sourceInvoice.customerListBatch || await createCustomerListInvoices(sourceInvoice, invoice);
  const sent = [];
  const failed = [...batch.failed, ...batch.missingEmail.map((entry) => ({ ...entry, reason: t('sale.list.createdNoEmail', 'Fakturaen er opprettet, men kunden mangler e-post. Send eller skriv den ut manuelt.') }))];
  const state = mergeState(runtime.appState);
  for (const created of batch.invoices.filter((entry) => !batch.missingEmail.some((missing) => missing.invoiceId === entry.id))) {
    const current = safeArray(state.invoices).find((entry) => entry.id === created.id) || created;
    try {
      const result = await sendInvoiceDocument(current, { ...options, onStage: undefined });
      sent.push({ customerId: current.customerId, customerName: current.customer?.name, invoice: result.invoice, email: result.email });
    } catch (error) {
      failed.push({ customerId: current.customerId, name: current.customer?.name, reason: error?.message || t('sale.send.failed', 'Sendingen feilet.') });
    }
  }
  emitStage('invoice', 'Utkast klargjøres', 'done');
  emitStage('sent', 'Suksess! Postplumma er på vei.', sent.length ? 'done' : 'error');
  const names = failed.map((entry) => entry.name || entry.customerId).filter(Boolean).join(', ');
  if (!sent.length) throw new Error(failed.length ? t('sale.list.noneSent', 'Fakturaene er opprettet, men ingen ble sendt på e-post. Send eller skriv ut manuelt: {names}', { names }) : t('svc.noCustomerEmails'));
  return {
    ok: failed.length === 0,
    batch: true,
    emailSent: sent.length > 0,
    sentCount: sent.length,
    failedCount: failed.length,
    invoices: sent.map((entry) => entry.invoice).filter(Boolean),
    failedRecipients: failed,
    email: {
      to: t('sale.list.recipientsCount', '{count} mottakere', { count: sent.length }),
      fromName: sent[0]?.email?.fromName || '',
      fromEmail: sent[0]?.email?.fromEmail || '',
      replyTo: sent[0]?.email?.replyTo || '',
    },
    message: failed.length
      ? t('sale.list.sentPartly', 'Sendt til {sent} mottakere. Ikke sendt: {names}', { sent: sent.length, names })
      : t('sale.list.sentAll', 'Sendt til {sent} mottakere.', { sent: sent.length }),
  };
};

const sendOtherSaleDocument = async (saleInput, options = {}) => {
  const onStage = typeof options.onStage === 'function' ? options.onStage : null;
  const emitStage = (key, label, status = 'done', message = '') => {
    onStage?.({ key, label, status, message, at: nowIso() });
  };
  const runStage = async (key, label, action) => {
    emitStage(key, label, 'active');
    try {
      const result = await action();
      emitStage(key, label, 'done');
      return result;
    } catch (error) {
      emitStage(key, label, 'error', error?.message || t('bam.inv.stepFailed'));
      throw error;
    }
  };

  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const saleId = String(saleInput?.id || '').trim();
  if (!saleId) throw new Error(t('svc.missingSaleToSend'));

  const state = mergeState(runtime.appState);
  const existingSale = safeArray(state.otherSales).find((entry) => entry.id === saleId);
  const sale = stripUndefinedDeep({
    ...(existingSale || {}),
    ...saleInput,
    status: 'completed',
    voucherNumber: saleInput.voucherNumber || existingSale?.voucherNumber || otherSaleVoucherNumberFromId(saleId),
  });
  const settings = state.settings;
  const customer = findCustomerForOtherSale(sale, state);
  const saleWithCustomer = stripUndefinedDeep({
    ...sale,
    customer: customer || sale.customer || undefined,
    customerId: sale.customerId || customer?.id || undefined,
  });

  if (!saleWithCustomer.customer?.email) {
    throw new Error(t('svc.customerNoEmail'));
  }
  if (!settings.email) {
    throw new Error(t('svc.businessNoEmail'));
  }

  const storedSale = await runStage('sale', 'Utkast klargjøres', () => upsertOtherSale(saleWithCustomer));
  const receiptInvoice = buildOtherSaleReceiptInvoice(storedSale, settings);
  const pdf = await runStage('pdf-render', 'Postplumma ankommer', () => getInvoicePdfFromBackend(receiptInvoice, settings, 'other_sale_receipt', { storePdf: true }));
  const storedPdf = backendPdfStorageInfo(pdf, receiptInvoice);
  emitStage('pdf-store', 'Kvitteringen er klar', 'done');
  const pdfGeneratedAt = nowIso();
  const saleWithPdf = stripUndefinedDeep({
    ...storedSale,
    customer: customer || storedSale.customer || undefined,
    customerId: storedSale.customerId || customer?.id || undefined,
    receiptPdfStoragePath: storedPdf.storagePath || undefined,
    receiptPdfDownloadUrl: storedPdf.downloadUrl || undefined,
    receiptPdfFileName: storedPdf.fileName,
    receiptPdfMimeType: 'application/pdf',
    receiptPdfGeneratedAt: pdfGeneratedAt,
  });
  await upsertOtherSale(saleWithPdf);

  const emailResult = await runStage('email', 'Postplumma sender e-posten', () => authorizedPost('sendInvoiceEmailHttp', {
    invoice: buildOtherSaleReceiptInvoice(saleWithPdf, settings),
    settings,
    variant: 'other_sale_receipt',
    deliveryMode: 'attachment',
    businessId: runtime.activeBusinessId,
  }));
  const emailMeta = stripUndefinedDeep({
    to: emailResult.to || saleWithPdf.customer?.email || '',
    fromEmail: emailResult.fromEmail || '',
    fromName: emailResult.fromName || settings.businessName || 'Plumme',
    replyTo: emailResult.replyTo || settings.email || '',
    subject: emailResult.subject || '',
  });

  const sentAt = nowIso();
  const sentSale = stripUndefinedDeep({
    ...saleWithPdf,
    sentAt: saleWithPdf.sentAt || sentAt,
    sendEvents: [
      ...safeArray(saleWithPdf.sendEvents),
      {
        id: emailResult.emailId || `send${Date.now()}`,
        sentAt,
        variant: 'other_sale_receipt',
        deliveryMode: 'attachment',
        source: 'plumme-backend',
        to: emailMeta.to,
        fromEmail: emailMeta.fromEmail,
        fromName: emailMeta.fromName,
        replyTo: emailMeta.replyTo,
      },
    ],
  });
  const savedSale = await upsertOtherSale(sentSale);
  emitStage('sent', 'Suksess! Postplumma er på vei.');
  return {
    ok: true,
    emailSent: true,
    otherSale: savedSale,
    emailId: emailResult.emailId,
    email: emailMeta,
    message: t('bam.inv.receiptSentByEmail'),
  };
};

const setOpenInvoice = (invoice) => {
  window.PlummeBackend.openInvoice = invoice;
};

const consumeOpenInvoice = () => {
  const invoice = window.PlummeBackend.openInvoice || null;
  window.PlummeBackend.openInvoice = null;
  return invoice;
};

const setOpenCustomerConversation = (target) => {
  window.PlummeBackend.openCustomerConversation = target || null;
};

const consumeOpenCustomerConversation = () => {
  const target = window.PlummeBackend.openCustomerConversation || null;
  window.PlummeBackend.openCustomerConversation = null;
  return target;
};

const openInvoiceDocument = async (uiInvoice) => {
  const viewer = window.open('', '_blank');
  try {
    const pdf = await ensureInvoicePdf(uiInvoice);
    if (pdf.downloadUrl) {
      if (viewer) viewer.location.href = pdf.downloadUrl;
      else window.open(pdf.downloadUrl, '_blank', 'noopener,noreferrer');
      return pdf;
    }
    const blob = pdfBase64ToBlob(pdf.pdfBase64);
    const url = URL.createObjectURL(blob);
    if (viewer) viewer.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    return pdf;
  } catch (error) {
    if (viewer) viewer.close();
    throw error;
  }
};

const getInvoicePdfPreview = async (uiInvoice) => {
  const pdf = await ensureInvoicePdf(uiInvoice);
  if (pdf.downloadUrl) return { src: pdf.downloadUrl, name: pdf.fileName || 'faktura.pdf', objectUrl: false };
  if (pdf.pdfBase64) return { src: URL.createObjectURL(pdfBase64ToBlob(pdf.pdfBase64)), name: pdf.fileName || 'faktura.pdf', objectUrl: true };
  throw new Error(t('svc.invoicePdfNotFound'));
};

const getPackingSlipInvoice = (uiInvoice, options = {}) => {
  const invoice = resolveBackendInvoice(uiInvoice);
  if (!invoice?.id) throw new Error(t('svc.missingInvoice'));
  if (invoice.kind !== 'invoice') throw new Error(t('svc.packingSlipInvoiceOnly'));
  return stripUndefinedDeep({
    ...invoice,
    packingSlipDate: options.shippingDate || options.date || new Date().toISOString().slice(0, 10),
  });
};

const getPackingSlipPdfPreview = async (uiInvoice, options = {}) => {
  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const invoice = getPackingSlipInvoice(uiInvoice, options);
  const settings = mergeState(runtime.appState).settings;
  const pdf = await getInvoicePdfFromBackend(invoice, settings, 'packing_slip', { storePdf: false });
  if (pdf.pdfBase64) return { src: URL.createObjectURL(pdfBase64ToBlob(pdf.pdfBase64)), name: pdf.fileName || 'pakkseddel.pdf', objectUrl: true };
  throw new Error(t('svc.packingSlipPdfNotFound'));
};

const downloadPackingSlip = async (uiInvoice, options = {}) => {
  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const invoice = getPackingSlipInvoice(uiInvoice, options);
  const settings = mergeState(runtime.appState).settings;
  const pdf = await getInvoicePdfFromBackend(invoice, settings, 'packing_slip', { storePdf: false });
  if (!pdf.pdfBase64) throw new Error(t('svc.packingSlipPdfNotFound'));
  downloadBlob(pdfBase64ToBlob(pdf.pdfBase64), pdf.fileName || 'pakkseddel.pdf');
  return pdf;
};

const downloadInvoice = async (uiInvoice) => {
  let downloadPromise = null;
  const pdf = await ensureInvoicePdf(uiInvoice, { onReady: (ready) => {
    if (ready.downloadUrl) downloadPromise = downloadUrlAsFile(ready.downloadUrl, ready.fileName);
    else if (ready.pdfBase64) {
      downloadBlob(pdfBase64ToBlob(ready.pdfBase64), ready.fileName); downloadPromise = Promise.resolve();
    }
    else downloadPromise = Promise.reject(new Error(t('svc.invoicePdfNotFound')));
  } });
  await downloadPromise;
  return pdf;
};

const getInvoicePublicLink = async (uiInvoice) => {
  const invoice = resolveBackendInvoice(uiInvoice);
  if (!invoice?.id) throw new Error(t('svc.missingInvoice'));
  const result = await authorizedPost('invoicePublicLinkHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: invoice.id,
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  if (!result.url) throw new Error(t('svc.noInvoiceLink'));
  return result.url;
};

const getInvoicePublicView = async ({ businessId, invoiceId, token } = {}) => {
  if (!String(businessId || '').trim() || !String(invoiceId || '').trim() || !String(token || '').trim()) {
    throw new Error(t('svc.invalidInvoiceLink', 'Ugyldig fakturalenke.'));
  }
  const result = await publicPost('invoicePublicViewHttp', { businessId, invoiceId, token });
  if (!result?.invoice) throw new Error(t('svc.invoiceLinkNotFound', 'Fakturaen ble ikke funnet.'));
  return result;
};

const resolveBackendVoucher = (voucher) => {
  if (!voucher?.sourceType || !voucher?.sourceId) return voucher || null;
  const state = mergeState(runtime.appState);
  if (voucher.sourceType === 'invoice') {
    const invoice = safeArray(state.invoices).find((entry) => entry.id === voucher.sourceId) || voucher.source;
    return { ...voucher, source: invoice, attachments: voucherAttachments(invoice || voucher.source || voucher) };
  }
  if (voucher.sourceType === 'expense' || voucher.sourceType === 'expensePayment') {
    const expense = safeArray(state.expenses).find((entry) => entry.id === voucher.sourceId) || voucher.source;
    return { ...voucher, source: expense, attachments: voucherAttachments(expense || voucher.source || voucher) };
  }
  if (voucher.sourceType === 'otherSale') {
    const sale = safeArray(state.otherSales).find((entry) => entry.id === voucher.sourceId) || voucher.source;
    return { ...voucher, source: sale, attachments: voucherAttachments(sale || voucher.source || voucher) };
  }
  return voucher;
};

const getVoucherAttachmentUrl = async (attachment) => {
  if (attachment?.downloadUrl) return attachment.downloadUrl;
  if (attachment?.storagePath && runtime.storage) {
    const url = await runtime.storage.ref().child(attachment.storagePath).getDownloadURL().catch(() => '');
    if (url) return url;
  }
  if (attachment?.id && runtime.storage && runtime.user?.uid) {
    const legacyPath = `users/${runtime.user.uid}/attachments/${attachment.id}`;
    const url = await runtime.storage.ref().child(legacyPath).getDownloadURL().catch(() => '');
    if (url) return url;
  }
  return '';
};

const firstVoucherAttachment = (voucher) => safeArray(voucher?.attachments)[0] || null;
const inferAttachmentMimeType = (attachment) => {
  const mime = String(attachment?.mimeType || '').trim();
  if (mime) return mime;
  const name = String(attachment?.name || '').toLowerCase();
  if (/\.pdf$/.test(name)) return 'application/pdf';
  if (/\.(jpg|jpeg)$/.test(name)) return 'image/jpeg';
  if (/\.png$/.test(name)) return 'image/png';
  if (/\.webp$/.test(name)) return 'image/webp';
  if (/\.gif$/.test(name)) return 'image/gif';
  return '';
};

const getVoucherAttachmentPreview = async (attachment, url, voucher) => {
  const name = attachment.name || `${voucher?.num || voucher?.id || 'bilag'}`;
  const fallbackMimeType = inferAttachmentMimeType(attachment);
  if (/^(image\/|application\/pdf$)/.test(fallbackMimeType)) {
    return {
      src: url,
      url,
      name,
      mimeType: fallbackMimeType,
    };
  }
  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return {
      src: URL.createObjectURL(blob),
      url,
      objectUrl: true,
      name,
      mimeType: blob.type || fallbackMimeType,
    };
  } catch (error) {
    return {
      src: url,
      url,
      name,
      mimeType: fallbackMimeType,
    };
  }
};

const openVoucherDocument = async (voucher, selectedAttachment = null) => {
  const resolved = resolveBackendVoucher(voucher);
  if (!resolved?.id) throw new Error(t('svc.missingVoucher'));
  if (resolved.sourceType === 'invoice' && !selectedAttachment) {
    const preview = await getInvoicePdfPreview(resolved.source || resolved.sourceId);
    return { ...preview, voucher: resolved, mimeType: 'application/pdf' };
  }
  const attachment = selectedAttachment || firstVoucherAttachment(resolved);
  if (!attachment) throw new Error(t('svc.voucherNoAttachment'));
  const url = await getVoucherAttachmentUrl(attachment);
  if (!url) throw new Error(t('svc.voucherFileNotFound'));
  return {
    ...(await getVoucherAttachmentPreview(attachment, url, resolved)),
    voucher: resolved,
    attachment,
  };
};

const downloadVoucherDocument = async (voucher, selectedAttachment = null) => {
  const resolved = resolveBackendVoucher(voucher);
  if (!resolved?.id) throw new Error(t('svc.missingVoucher'));
  if (resolved.sourceType === 'invoice' && !selectedAttachment) {
    return downloadInvoice(resolved.source || resolved.sourceId);
  }
  if (resolved.sourceType === 'otherSale' && !selectedAttachment) {
    return downloadOtherSaleReceiptDocument(resolved.source || resolved.sourceId || resolved);
  }
  const attachment = selectedAttachment || firstVoucherAttachment(resolved);
  if (!attachment) throw new Error(t('svc.voucherNoAttachment'));
  const url = await getVoucherAttachmentUrl(attachment);
  if (!url) throw new Error(t('svc.voucherFileNotFound'));
  await downloadUrlAsFile(url, attachment.name || `${resolved.num || resolved.id}.pdf`);
  return { voucher: resolved, attachment, url };
};

const MIME_FILE_EXTENSIONS = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/heic': '.heic',
  'text/plain': '.txt',
  'text/csv': '.csv',
};

// Filnavnet på et vedlegg kommer fra opplastingen og har som regel endelse
// allerede. Bilag som er kommet inn på e-post kan mangle den, og en fil uten
// endelse åpner ikke av seg selv etter utpakking.
const inboxAttachmentFileName = (entry, attachment, index) => {
  const raw = String(attachment?.name || entry?.attachmentName || entry?.name || '').trim();
  const base = raw || `bilag-${index + 1}`;
  if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;
  const extension = MIME_FILE_EXTENSIONS[String(attachment?.mimeType || entry?.attachmentMimeType || '').toLowerCase()] || '';
  return `${base}${extension}`;
};

const inboxAttachmentTargets = (entries) => {
  const targets = [];
  safeArray(entries).forEach((entry) => {
    const attachments = safeArray(entry?.attachments).length
      ? safeArray(entry.attachments)
      : [{
          id: entry?.attachmentId || '',
          name: entry?.attachmentName || entry?.name,
          mimeType: entry?.attachmentMimeType || '',
          downloadUrl: entry?.file || '',
          storagePath: entry?.storagePath || '',
        }].filter((attachment) => attachment.downloadUrl || attachment.storagePath || attachment.id);
    attachments.forEach((attachment, index) => {
      targets.push({ attachment, name: inboxAttachmentFileName(entry, attachment, index) });
    });
  });
  return targets;
};

// Nedlasting av flere bilag samtidig. Ett vedlegg lastes ned som seg selv;
// flere pakkes i én zip, slik at nettleseren slipper å be om tillatelse til
// mange nedlastinger etter hverandre.
const downloadInboxAttachmentsArchive = async (entries, { fileName } = {}) => {
  const targets = inboxAttachmentTargets(entries);
  if (!targets.length) throw new Error(t('svc.inboxNothingToDownload'));

  const files = [];
  const failed = [];
  for (const target of targets) {
    try {
      const url = await getVoucherAttachmentUrl(target.attachment);
      if (!url) throw new Error('missing-url');
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      files.push({ name: target.name, data: await response.blob() });
    } catch (error) {
      failed.push(target.name);
    }
  }
  if (!files.length) throw new Error(t('svc.inboxDownloadFailed'));

  if (files.length === 1) {
    downloadBlob(files[0].data, files[0].name);
    return { downloaded: 1, failed, zipped: false };
  }
  const archive = await window.PlummeZipArchive.createZipBlob(files);
  downloadBlob(archive, safeFilePart(fileName || 'bilag.zip', 'bilag.zip'));
  return { downloaded: files.length, failed, zipped: true };
};

const markVoucherAttachmentLostLocal = async (voucher, options = {}) => {
  const resolved = resolveBackendVoucher(voucher);
  if (resolved?.sourceType !== 'expense' || !resolved?.sourceId) throw new Error(t('bam.inv.lostOnlyPostedPurchases'));
  const reason = String(options.reason || options.attachmentMissingReason || '').trim().slice(0, 500);
  const now = nowIso();
  let nextExpense = null;
  await updateStateAndPersist((state) => ({
    ...state,
    expenses: safeArray(state.expenses).map((expense) => {
      if (expense.id !== resolved.sourceId) return expense;
      if (expense.status === 'draft' || expense.status === 'annulled') return expense;
      if (hasPrimaryAttachment(expense)) return expense;
      nextExpense = stripUndefinedDeep({
        ...expense,
        attachmentStatus: 'missing_lost',
        attachmentMissingReason: reason || expense.attachmentMissingReason,
        attachmentMissingMarkedAt: now,
        attachmentMissingHistory: [
          ...safeArray(expense.attachmentMissingHistory),
          stripUndefinedDeep({ status: 'missing_lost', reason: reason || undefined, markedAt: now }),
        ],
        updatedAt: now,
      });
      return nextExpense;
    }),
  }));
  if (!nextExpense) throw new Error(t('bam.inv.noActivePurchaseWithoutVoucher'));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return nextExpense;
};

const markVoucherAttachmentLost = async (voucher, options = {}) => {
  const resolved = resolveBackendVoucher(voucher);
  if (resolved?.sourceType !== 'expense' || !resolved?.sourceId) throw new Error(t('bam.inv.lostOnlyPostedPurchases'));
  try {
    const result = await authorizedPost('purchaseMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'mark_attachment_lost',
      expenseId: resolved.sourceId,
      expense: { reason: options.reason || options.attachmentMissingReason || '' },
    });
    if (result?.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return result.expense || null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return markVoucherAttachmentLostLocal(resolved, options);
  }
};

const annulCustomsDeclaration = async (entryOrId, options = {}) => {
  const entryId = typeof entryOrId === 'string' ? entryOrId : entryOrId?.id;
  if (!entryId) throw new Error(t('svc.missingCustomsToCancel'));
  const comment = String(options.annulComment || options.comment || '').trim();
  const result = await authorizedPost('vatMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'annul_customs', entryId, comment,
  });
  applyVatMutationResult(result);
  return result.entry;
};

const annulVoucher = async (voucher, options = {}) => {
  const resolved = resolveBackendVoucher(voucher);
  if (!resolved?.sourceType || !resolved?.sourceId) throw new Error(t('svc.missingVoucherToCancel'));
  if (['annulled', 'annullert', 'reversed'].includes(resolved.status)) return resolved;
  if(resolved.sourceType==='yearEndEntry') {
    const entry=Object.values(runtime.appState?.yearEndEntriesByYear||{}).flatMap(safeArray).find(row=>row.id===resolved.sourceId);
    if(['fixed_asset_impairment','fixed_asset_impairment_reversal','fixed_asset_reversal'].includes(entry?.kind)&&entry.calculation?.assetIds?.length===1) return mutateFixedAsset({operation:entry.kind==='fixed_asset_reversal'?'annul_depreciation_reversal':'annul_impairment',assetId:entry.calculation.assetIds[0],entryId:entry.id,date:todayIso(),note:String(options.annulComment||'').trim()});
  }
  if (resolved.sourceType === 'invoice') {
    const invoice = resolved.source || safeArray(runtime.appState?.invoices).find((entry) => entry.id === resolved.sourceId);
    if (!invoice?.id) throw new Error(t('svc.invoiceToCreditNotFound'));
    if (invoice.kind !== 'invoice') throw new Error(t('svc.cannotCancelHere'));
    const credit = await creditInvoice({ ...mapInvoiceToUi(invoice, mergeState(runtime.appState)), _backend: invoice });
    return { credit, voucher: resolved, requiresCreditDraft: true };
  }
  if (resolved.sourceType === 'expense' || resolved.sourceType === 'expensePayment') {
    const expense = resolved.source || safeArray(runtime.appState?.expenses).find((entry) => entry.id === resolved.sourceId);
    if (expense?.manualPosting) return annulManualPosting(expense, options);
    return annulPurchase(resolved.sourceId, options);
  }
  if (resolved.sourceType === 'otherSale') return annulOtherSale(resolved.sourceId, options);
  if (resolved.sourceType === 'customsDeclaration') return annulCustomsDeclaration(resolved.sourceId, options);
  throw new Error(t('svc.voucherTypeNotCancellable'));
};

const copyInvoice = async (uiInvoice) => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingInvoiceToCopy'));
  const result = await authorizedPost('copyInvoiceHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: backend.id,
  });
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  return result.invoice || null;
};

// Linjene en ny kreditnota skal bestaa av, gitt hva som allerede er kreditert paa
// fakturaen. Ren funksjon, uten backend-kall, slik at grensen kan testes direkte.
const buildCreditNoteLines = (state, backend, options = {}) => {
  const selectionsByLineId = new Map(safeArray(options.selections).map((selection) => [
    String(selection.sourceLineId || ''),
    {
      quantity: Math.max(0, num(selection.quantity)),
      netAmount: selection.netAmount == null ? null : Math.max(0, num(selection.netAmount)),
    },
  ]));
  const hasSelections = selectionsByLineId.size > 0;
  // Det som allerede er kreditert er ikke lenger til aa kreditere. Taket ligger her og
  // ikke bare i dialogen, slik at massekreditering, bilagsannullering og assistenten
  // treffer samme grense: en faktura kan aldri krediteres for mer enn den lyder paa.
  const alreadyCredited = invoiceCreditedByLine(state, backend.id);
  let hadRemaining = false;
  const creditLines = safeArray(backend.lines).flatMap((line, index) => {
    const sourceLineId = String(line.id || `line-${index + 1}`);
    const taken = alreadyCredited.get(sourceLineId) || { quantity: 0, net: 0 };
    const originalQuantity = Math.abs(num(line.quantity, 1));
    const originalNet = Math.abs(invoiceLineNet(line));
    // Resten maales i kroner. Antallet er avledet av den andelen, slik at en linje som
    // ble delkreditert med et belop ikke framstaar som oppbrukt fordi antallet var 1.
    const remainingNet = Math.max(0, backendRoundMoney(originalNet - taken.net));
    const remainingQuantity = originalNet > 0.005 ? originalQuantity * (remainingNet / originalNet) : 0;
    if (remainingQuantity <= 0 || remainingNet <= 0.005) return [];
    hadRemaining = true;
    if (hasSelections && !selectionsByLineId.has(sourceLineId)) return [];
    const selection = selectionsByLineId.get(sourceLineId) || {};
    const selectedQuantity = hasSelections
      ? Math.min(remainingQuantity, selection.quantity || 0)
      : remainingQuantity;
    if (selectedQuantity <= 0) return [];
    const requestedNet = selection.netAmount == null
      ? originalNet * (selectedQuantity / (originalQuantity || 1))
      : selection.netAmount;
    const netAmount = Math.min(remainingNet, Math.max(0, requestedNet));
    if (netAmount <= 0.005) return [];
    // En urort linje som krediteres i sin helhet beholder pris og rabatt slik de staar
    // paa fakturaen. Bare naar belopet faktisk avviker regnes en ny enhetspris ut.
    const isUntouchedFullLine = taken.net <= 0.005
      && Math.abs(selectedQuantity - originalQuantity) <= 0.0000001
      && Math.abs(netAmount - originalNet) <= 0.005;
    return [{
      ...line,
      id: `${sourceLineId}-credit-${Date.now()}-${index + 1}`,
      creditedFromLineId: sourceLineId,
      quantity: selectedQuantity,
      unitPrice: isUntouchedFullLine ? -Math.abs(num(line.unitPrice)) : -(netAmount / selectedQuantity), documentUnitPrice: line.documentUnitPrice == null ? undefined : (isUntouchedFullLine ? -Math.abs(num(line.documentUnitPrice)) : -(Math.abs(num(line.documentUnitPrice)) * (netAmount / originalNet))),
      discountValue: isUntouchedFullLine ? line.discountValue : 0,
      discountType: isUntouchedFullLine ? line.discountType : 'percent',
    }];
  });
  if (!creditLines.length) {
    throw new Error(hadRemaining ? t('svc.pickLineToCredit') : t('svc.invoiceFullyCredited'));
  }
  return creditLines;
};

// Aarsaken kreditnotaen skal baere. Bokfoeringsforskriften § 5-2-7 krever at en
// kreditnota viser hva den gjelder, og bokfoeringsloven § 10 at grunnlaget viser det
// reelle innholdet — derfor er den paakrevd naar kreditnotaen faktisk bokfoeres, og
// bare anbefalt saa lenge den ligger som utkast.
const assertCreditReasonForPosting = (reason) => {
  if (String(reason || '').trim()) return;
  throw new Error(t('svc.creditReasonRequired'));
};

const creditInvoice = async (uiInvoice, options = {}) => {
  const backend = uiInvoice._backend || mapUiInvoiceToBackend(uiInvoice);
  const creditLines = buildCreditNoteLines(runtime.appState, backend, options);
  const creditReason = String(options.creditReason || '').trim();
  const minimumDate = String(backend.date || '').slice(0, 10); const creditDate = String(options.date || minimumDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(creditDate)) throw new Error(t('inv.credit.invalidDate')); if (minimumDate && creditDate < minimumDate) throw new Error(t('inv.credit.dateBeforeInvoice'));
  // `issue` posts and numbers the credit immediately, so the reason is required.
  const issue = !!options.issue;
  if (issue) assertCreditReasonForPosting(creditReason);
  const credit = {
    ...backend,
    id: `credit${Date.now()}`, fixedAssetSale: undefined,
    invoiceNumber: '',
    kind: 'credit',
    status: issue ? 'completed' : 'draft',
    // `null`, ikke `undefined`: et felt som mangler i payloaden overskriver ingenting
    // i sammenslaaingen paa serveren, og dokumentet ville beholdt utkastflagget.
    invoiceType: issue ? null : backend.invoiceType,
    createdAt: nowIso(),
    date: creditDate,
    creditedFromInvoiceId: backend.id,
    creditReason: creditReason || undefined,
    creditReference: String(backend.invoiceNumber || '').trim() || undefined,
    lines: creditLines,
    paidAt: undefined,
    sentAt: undefined,
  };
  assertCreditInvoiceMutationAllowed(credit); if(options.v)return credit;
  const result = await authorizedPost('upsertInvoiceHttp', {
    businessId: runtime.activeBusinessId,
    invoice: credit,
  });
  if (result.invoice) {
    replaceInvoiceLocal(result.invoice);
    return result.invoice;
  }
  return null;
};

// Et utkast blir et dokument. Samme vei som «Opprett» i salgseditoren: status settes
// til `completed`, og backend tildeler dokumentnummer og bokforer. Kravene editoren
// stiller for den knappen gjelder ogsaa her — et utkast uten kunde eller linjer er
// ikke et dokument, og en kreditnota uten aarsak mangler grunnlaget den skal ha.
const issueInvoiceDraft = async (uiInvoice, options = {}) => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingInvoiceToSave'));
  if (backend.status !== 'draft') return backend;
  // «Uten kunde» er navnet et utkast far naar ingen kunde er valgt, ikke en kunde.
  const customerName = String(backend.customer?.name || '').trim();
  if (!customerName || customerName === 'Uten kunde') throw new Error(t('svc.pickCustomerForDoc'));
  if (!safeArray(backend.lines).some((line) => String(line?.description || line?.productName || '').trim())) {
    throw new Error(t('svc.addProductForDoc'));
  }
  const creditReason = String(backend.creditReason || '').trim() || String(options.creditReason || '').trim();
  if (backend.kind === 'credit') assertCreditReasonForPosting(creditReason);
  return upsertInvoice(stripUndefinedDeep({
    ...backend,
    status: 'completed',
    // `null`, ikke `undefined`: se kommentaren i `buildInvoiceFromEditor`.
    invoiceType: null,
    creditReason: backend.kind === 'credit' ? creditReason : backend.creditReason,
    updatedAt: nowIso(),
  }));
};

const archiveInvoice = async (uiInvoice, archived = true) => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingDocToArchive'));
  if (!['offer', 'order_confirmation'].includes(backend.kind)) {
    throw new Error(t('svc.onlyQuotesAndOrders'));
  }
  const next = stripUndefinedDeep({
    ...backend,
    archivedAt: archived ? (backend.archivedAt || nowIso()) : null,
  });
  return upsertInvoice(next);
};

// External delivery is a delivery fact, not an email event. Keep the explicit
// provenance while setting sentAt for the existing unread/unsent projections.
// No accounting fields are changed by this mutation.
const markInvoiceDeliveredExternally = async (uiInvoice) => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingInvoiceToSave'));
  if (backend.status === 'draft') throw new Error(t('svc.onlyCompletedDocumentsDelivered'));
  if (!['invoice', 'offer', 'order_confirmation', 'credit'].includes(backend.kind)) {
    throw new Error(t('svc.onlySalesDocumentsDelivered'));
  }
  const deliveredAt = nowIso();
  return upsertInvoice(stripUndefinedDeep({
    ...backend,
    sentAt: backend.sentAt || deliveredAt,
    deliveredExternallyAt: deliveredAt,
    deliverySource: 'external',
    updatedAt: deliveredAt,
  }));
};

// Et tilbud kan godtas muntlig, i et mote eller i en e-post. Da registreres
// aksepten her i stedet for i kundelenken, men kilden lagres slik at historikken
// viser hvem som faktisk trykket.
const markOfferAccepted = async (uiInvoice, accepted = true) => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingOffer', 'Fant ikke tilbudet.'));
  if (backend.kind !== 'offer') throw new Error(t('svc.onlyOffersAccepted', 'Bare tilbud kan settes som akseptert.'));
  if (!accepted && !window.PlummeOfferConversion.canUndoAcceptance(backend)) {
    throw new Error(t('svc.cannotUndoCustomerAcceptance', 'Aksept som kunden selv har registrert kan ikke fjernes.'));
  }
  const next = stripUndefinedDeep({
    ...backend,
    offerAcceptedAt: accepted ? (backend.offerAcceptedAt || nowIso()) : null,
    offerAcceptedSource: accepted ? (backend.offerAcceptedSource || 'manual') : null,
    offerAcceptedByEmail: accepted ? (backend.offerAcceptedByEmail || null) : null,
  });
  return upsertInvoice(next);
};

// Kopi som utkast i editoren, ikke i databasen. Kopien blir forst et utkast i
// lista naar brukeren lagrer den selv — ellers ville «Lag kopi» og «Lagre
// utkast» gitt to utkast for samme dokument.
const copyInvoiceForEditor = (uiInvoice, options = {}) => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingInvoiceToCopy'));
  return window.PlummeInvoiceDraftCopy.draftCopy(backend, {
    newId: `inv${Date.now()}`,
    now: nowIso(),
    ...options,
  });
};

// Tilbud -> faktura eller ordrebekreftelse. Samme lokale kopi, men med ny
// dokumenttype og en peker tilbake til tilbudet. Pekeren lagres foerst naar
// dokumentet lagres, saa et tilbud aldri ser fakturert ut av et utkast som ble
// aapnet og forlatt.
const documentDraftFromOffer = (uiInvoice, target = 'faktura') => {
  const backend = resolveBackendInvoice(uiInvoice);
  if (!backend?.id) throw new Error(t('svc.missingOffer', 'Fant ikke tilbudet.'));
  if (backend.kind !== 'offer') {
    throw new Error(t('svc.onlyOffersConverted', 'Bare tilbud kan gjøres om til faktura eller ordrebekreftelse.'));
  }
  const kind = window.PlummeOfferConversion.conversionKind(target);
  const existing = safeArray(runtime.appState?.invoices).find((entry) => String(entry?.convertedFromInvoiceId || '') === String(backend.id)
    && entry?.kind === kind && !['annulled', 'deleted'].includes(String(entry?.status || '').toLowerCase()));
  if (existing) throw new Error(t('svc.offerAlreadyConverted', 'Tilbudet er allerede konvertert til denne dokumenttypen.'));
  const draft = copyInvoiceForEditor(uiInvoice, { kind, convertedFromInvoiceId: backend.id });
  if (!draft) throw new Error(t('svc.offerConversionFailed', 'Kunne ikke opprette dokumentet fra tilbudet.'));
  const date = draft.date || nowIso().slice(0, 10);
  // Tilbudets «gyldig til» er ikke en forfallsdato. Fakturaen faar vanlig
  // betalingsfrist, og ordrebekreftelsen har ingen av delene.
  delete draft.validUntil;
  draft.date = date;
  if (kind === 'invoice') draft.dueDate = draft.dueDate || window.PlummeInvoicePaymentTerms.dueDate(date, runtime.appState?.settings);
  else delete draft.dueDate;
  return draft;
};

const isDraftInvoiceForDelete = (invoice) => {
  if (!invoice) return false;
  if (invoice.invoiceType !== undefined) return invoice.invoiceType === 'draft';
  return invoice.status === 'draft';
};

const deleteInvoice = async (invoiceOrId) => {
  const existing = resolveBackendInvoice(invoiceOrId);
  if (!existing?.id) throw new Error(t('svc.missingInvoiceToDelete'));
  if (!isDraftInvoiceForDelete(existing)) return false;
  await authorizedPost('deleteInvoiceHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: existing.id,
  });
  deleteInvoiceLocal(existing.id);
  // Timene som laa til grunn for utkastet skal tilbake i fakturagrunnlaget.
  // Uten dette ville de blitt staaende som fakturert paa en faktura som ikke
  // finnes, og aldri kommet med paa et salg.
  await releaseTimeEntriesFromInvoice(existing.id).catch(() => 0);
  return true;
};

const createPaymentId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `payment-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const addInvoicePayment = async (invoiceOrId, paymentInput = {}) => {
  const existing = resolveBackendInvoice(invoiceOrId);
  if (!existing?.id) throw new Error(t('svc.missingInvoiceForPayment'));
  if (existing.kind === 'offer' || existing.kind === 'order_confirmation') {
    throw new Error(t('svc.quotesCannotBePaid'));
  }
  assertBankOpenForPayments(mergeState(runtime.appState), [{
    ...paymentInput,
    date: paymentInput.date || todayIso(),
    settlementAccountCode: paymentInput.settlementAccountCode || paymentInput.account || existing.bankAccountCode || '1920',
  }]);
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: existing.id,
    action: paymentInput.settlementKind === 'no_payment' ? 'settle_without_payment' : 'add',
    payment: {
      ...paymentInput,
      id: paymentInput.id || createPaymentId(),
      date: paymentInput.date || todayIso(),
    },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.payment || null;
};

const replaceInvoicePayment = async (paymentId, paymentInput = {}) => {
  if (!paymentId) throw new Error(t('svc.missingPaymentToReverse'));
  const payment = safeArray(runtime.appState?.payments).find((entry) => entry.id === paymentId);
  if (!payment?.documentId) throw new Error(t('svc.paymentToReverseNotFound'));
  assertBankOpenForPayments(mergeState(runtime.appState), [payment, {
    ...paymentInput,
    date: paymentInput.date || todayIso(),
    settlementAccountCode: paymentInput.settlementAccountCode || paymentInput.account || '1920',
  }]);
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: payment.documentId,
    action: 'replace_one',
    payment: {
      ...paymentInput,
      id: paymentInput.id || createPaymentId(),
      paymentId,
      date: paymentInput.date || todayIso(),
    },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.payment || null;
};

const allocateCustomerCredit = async ({ invoiceId, creditNoteId, amount, date, note } = {}) => {
  if (!invoiceId || !creditNoteId) throw new Error(t('bam.inv.missingOffsetDocs'));
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId,
    action: 'allocate_credit',
    payment: {
      id: createPaymentId(),
      creditNoteId,
      amount,
      date: date || todayIso(),
      note: note || 'Motregnet kundetilgode mot faktura',
    },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.settlement || null;
};

const reverseCustomerCreditAllocation = async ({ invoiceId, settlementId } = {}) => {
  if (!invoiceId || !settlementId) throw new Error(t('bam.inv.missingOffsetToReverse'));
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId,
    action: 'reverse_credit_allocation',
    payment: { settlementId },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return true;
};

const refundCustomerOverpayment = async ({ invoiceId, amount, date, settlementAccountCode, note, feeAmount, feeAccountCode, id } = {}) => {
  if (!invoiceId) throw new Error(t('bam.inv.missingRefundInvoice'));
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId,
    action: 'refund_overpayment',
    payment: { id: id || createPaymentId(), amount, date: date || todayIso(), settlementAccountCode: settlementAccountCode || '1920', note, feeAmount, feeAccountCode },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.payment || null;
};

const allocateCustomerOverpayment = async ({ invoiceId, sourceInvoiceId, amount, date, note } = {}) => {
  if (!invoiceId || !sourceInvoiceId) throw new Error(t('bam.inv.missingAllocationInvoices'));
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId,
    action: 'allocate_overpayment',
    payment: { id: createPaymentId(), sourceInvoiceId, amount, date: date || todayIso(), note },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.settlement || null;
};

const reverseCustomerOverpaymentAllocation = async ({ invoiceId, settlementId } = {}) => {
  if (!invoiceId || !settlementId) throw new Error(t('bam.inv.missingAllocationToReverse'));
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId,
    action: 'reverse_overpayment_allocation',
    payment: { settlementId },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return true;
};

const reverseInvoicePayment = async (paymentId) => {
  if (!paymentId) throw new Error(t('svc.missingPaymentToReverse'));
  const payment = safeArray(runtime.appState?.payments).find((entry) => entry.id === paymentId);
  if (!payment?.documentId) throw new Error(t('svc.paymentToReverseNotFound'));
  assertBankOpenForPayments(mergeState(runtime.appState), [payment]);
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: payment?.documentId,
    action: 'reverse_one',
    payment: { paymentId },
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
};

const reverseInvoicePayments = async (invoiceOrId) => {
  const existing = resolveBackendInvoice(invoiceOrId);
  if (!existing?.id) throw new Error(t('svc.missingInvoice'));
  assertBankOpenForPayments(mergeState(runtime.appState), safeArray(runtime.appState?.payments).filter((payment)=>payment.documentType === 'invoice' && payment.documentId === existing.id && payment.status !== 'reversed'));
  const result = await authorizedPost('invoicePaymentMutationHttp', {
    businessId: runtime.activeBusinessId,
    invoiceId: existing.id,
    action: 'reverse_all',
  });
  if (result.state) runtime.appState = mergeAppMutationState(result.state);
  if (result.invoice) replaceInvoiceLocal(result.invoice);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
};

const markInvoicePaid = async (invoiceId, paid) => {
  const existing = resolveBackendInvoice(invoiceId);
  if (!existing?.id) throw new Error(t('svc.missingInvoice'));
  if (!paid) {
    await reverseInvoicePayments(existing);
    return;
  }
  const summary = invoicePaymentSummary(mergeState(runtime.appState), existing);
  if (summary.status !== 'paid' && summary.remainingAmount > 0.005) {
    await addInvoicePayment(existing, {
      amount: summary.remainingAmount,
      date: todayIso(),
      settlementAccountCode: '1920',
      createdByFlow: 'toggle',
    });
  }
};

const partyMatchText = (value) => String(value || '').trim().toLocaleLowerCase('nb-NO');
const manualPostingPartyMatches = (left, right) => {
  if (!left || !right || left.type !== right.type) return false;
  if (left.id && right.id) return String(left.id) === String(right.id);
  return Boolean(left.name && right.name && partyMatchText(left.name) === partyMatchText(right.name));
};
const manualPostingRefs = (postingOrPostings) => safeArray(Array.isArray(postingOrPostings) ? postingOrPostings : [postingOrPostings])
  .map((posting) => posting?.savedEntry || posting)
  .map((posting) => safeArray(runtime.appState?.expenses).find((entry) => entry?.manualPosting && String(entry.id) === String(posting?.id)) || posting)
  .filter((posting) => posting?.manualPosting || safeArray(posting?.manualPostingLines).length);
const manualPostingSettlementLegs = (posting) => {
  if (String(posting?.country || posting?.manualPostingCountry || currentCountry()).toUpperCase() !== 'NO') return [];
  return safeArray(posting?.manualPostingLines).flatMap((line, index) => {
    const legs = [];
    const fromParty = { type: line.fromCounterpartyType, id: line.fromCounterpartyId || '', name: line.fromCounterpartyName || '', number: line.fromCounterpartyNumber || '' };
    if (String(line.fromAccount) === '1500' && fromParty.type === 'customer' && num(line.fromAmount) > 0.005) {
      legs.push({ id: `${line.id || index}:from`, documentType: 'invoice', accountCode: '1500', party: fromParty, amount: num(line.fromAmount) });
    }
    const toParty = { type: line.toCounterpartyType, id: line.toCounterpartyId || '', name: line.toCounterpartyName || '', number: line.toCounterpartyNumber || '' };
    if (String(line.toAccount) === '2400' && toParty.type === 'supplier' && num(line.toAmount) > 0.005) {
      legs.push({ id: `${line.id || index}:to`, documentType: 'supplier_invoice', accountCode: '2400', party: toParty, amount: num(line.toAmount) });
    }
    return legs;
  });
};

const manualPostingPartyMatchesCustomer = (party, invoice) => {
  const customer = invoice?.customer || {};
  return Boolean(
    (party.id && (customer.id === party.id || invoice.customerId === party.id)) ||
    (party.name && partyMatchText(customer.name || invoice.customerName) === partyMatchText(party.name)),
  );
};

const manualPostingPartyMatchesSupplier = (party, expense) => Boolean(
  (party.id && expense?.supplierId === party.id) ||
  (party.name && partyMatchText(expense?.supplierName) === partyMatchText(party.name))
);

const manualPostingDocumentParty = (state, documentType, documentId) => {
  if (documentType === 'invoice') {
    const invoice = safeArray(state.invoices).find((entry) => String(entry.id) === String(documentId));
    return invoice ? { type: 'customer', id: invoice.customer?.id || invoice.customerId || '', name: invoice.customer?.name || invoice.customerName || '' } : null;
  }
  const expense = safeArray(state.expenses).find((entry) => String(entry.id) === String(documentId));
  return expense ? { type: 'supplier', id: expense.supplierId || '', name: expense.supplierName || '' } : null;
};

const manualPostingSettlementCapacity = (state, posting) => {
  const rows = [];
  manualPostingSettlementLegs(posting).forEach((leg) => {
    const existing = rows.find((row) => row.documentType === leg.documentType && manualPostingPartyMatches(row.party, leg.party));
    if (existing) {
      existing.postedAmount += leg.amount;
      existing.availableAmount += leg.amount;
    } else {
      rows.push({ documentType: leg.documentType, party: leg.party, accountCode: leg.accountCode, postedAmount: leg.amount, allocatedAmount: 0, availableAmount: leg.amount });
    }
  });
  safeArray(state.payments)
    .filter((payment) => payment.status !== 'reversed' && payment.settlementKind === 'no_payment' && String(payment.manualPostingId || '') === String(posting.id || ''))
    .forEach((payment) => {
      const party = manualPostingDocumentParty(state, payment.documentType, payment.documentId);
      const row = rows.find((entry) => entry.documentType === payment.documentType && manualPostingPartyMatches(entry.party, party));
      if (!row) return;
      row.allocatedAmount += Math.max(0, num(payment.amount));
      row.availableAmount -= Math.max(0, num(payment.amount));
    });
  return rows.map((row) => ({ ...row, availableAmount: Math.max(0, row.availableAmount) }));
};

const getManualPostingSettlementCandidates = (postingOrPostings) => {
  const state = mergeState(runtime.appState);
  return manualPostingRefs(postingOrPostings).filter((posting)=>posting.supplierCreditSettlementVersion!==1).flatMap((posting) => manualPostingSettlementCapacity(state, posting).flatMap((capacity) => {
    const party = capacity.party;
    const base = { postingId: posting.id, postingVoucherNumber: posting.voucherNumber || posting.id, postingDate: posting.date || '', availableAmount: backendRoundMoney(capacity.availableAmount) };
    if (capacity.documentType === 'invoice') {
      return safeArray(state.invoices)
        .filter((invoice) => invoice.kind !== 'credit' && isPostedInvoice(invoice, state) && manualPostingPartyMatchesCustomer(party, invoice))
        .map((invoice) => ({ invoice, summary: invoicePaymentSummary(state, invoice) }))
        .filter(({ summary }) => summary.remainingAmount > 0.005)
        .map(({ invoice, summary }) => {
          const maxAmount = Math.min(summary.remainingAmount, capacity.availableAmount);
          return stripUndefinedDeep({
          ...base,
          id: `${posting.id}:invoice:${invoice.id}`,
          documentType: 'invoice',
          documentId: invoice.id,
          partyType: 'customer',
          partyName: party.name || invoice.customer?.name || '',
          number: invoice.invoiceNumber || invoice.id,
          date: invoice.date || '',
          dueDate: invoice.dueDate || '',
          amount: backendRoundMoney(invoiceGross(invoice)),
          remainingAmount: backendRoundMoney(summary.remainingAmount),
          maxAmount: backendRoundMoney(maxAmount),
          suggestedAmount: backendRoundMoney(maxAmount),
          exactMatch: Math.abs(summary.remainingAmount - capacity.availableAmount) <= 0.005,
        });
        });
    }
    return safeArray(state.expenses)
      .filter((expense) => expense.isSupplierInvoice && isActiveExpense(expense) && manualPostingPartyMatchesSupplier(party, expense))
      .map((expense) => ({ expense, summary: dashboardSupplierInvoicePaymentSummary(state, expense) }))
      .filter(({ summary }) => summary.remainingAmount > 0.005)
      .map(({ expense, summary }) => {
        const maxAmount = Math.min(summary.remainingAmount, capacity.availableAmount);
        return stripUndefinedDeep({
        ...base,
        id: `${posting.id}:supplier_invoice:${expense.id}`,
        documentType: 'supplier_invoice',
        documentId: expense.id,
        partyType: 'supplier',
        partyName: party.name || expense.supplierName || '',
        number: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
        date: expense.date || '',
        dueDate: expense.dueDate || '',
        amount: backendRoundMoney(dashboardGrossExpense(expense)),
        remainingAmount: backendRoundMoney(summary.remainingAmount),
        maxAmount: backendRoundMoney(maxAmount),
        suggestedAmount: backendRoundMoney(maxAmount),
        exactMatch: Math.abs(summary.remainingAmount - capacity.availableAmount) <= 0.005,
      });
      });
  }));
};

const getManualPostingCandidatesForOpenItem = ({ documentType, documentId } = {}) => {
  const state = mergeState(runtime.appState);
  const party = manualPostingDocumentParty(state, documentType, documentId);
  if (!party) return [];
  return safeArray(state.expenses)
    .filter((posting) => posting.manualPosting && String(posting.status || 'active') === 'active')
    .flatMap((posting) => getManualPostingSettlementCandidates(posting)
      .filter((candidate) => candidate.documentType === documentType && String(candidate.documentId) === String(documentId)));
};

const settleSupplierInvoiceWithoutPaymentLocal = async (expenseOrId, paymentInput = {}) => {
  const expenseId = typeof expenseOrId === 'string' ? expenseOrId : expenseOrId?.id;
  if (!paymentInput.manualPostingId) throw new Error(t('bam.inv.settleNeedsFreePosting'));
  if (!expenseId) throw new Error(t('bam.inv.missingSupplierInvoiceSettle'));
  let nextExpense = null;
  let payment = null;
  await updateStateAndPersist((state) => {
    const expenses = safeArray(state.expenses);
    const index = expenses.findIndex((entry) => entry.id === expenseId);
    if (index < 0) throw new Error(t('bam.inv.supplierInvoiceNotFound'));
    const expense = expenses[index];
    const summary = dashboardSupplierInvoicePaymentSummary(state, expense);
    if (summary.remainingAmount <= 0.005) throw new Error(t('bam.inv.supplierInvoiceNothingRemaining'));
    const manualPosting = expenses.find((entry) => entry.manualPosting && entry.status === 'active' && String(entry.id) === String(paymentInput.manualPostingId));
    const capacity = manualPosting && manualPostingSettlementCapacity(state, manualPosting)
      .find((row) => row.documentType === 'supplier_invoice' && manualPostingPartyMatches(row.party, manualPostingDocumentParty(state, 'supplier_invoice', expense.id)));
    const amount = num(paymentInput.amount, summary.remainingAmount);
    if (!capacity || amount <= 0.005 || amount > summary.remainingAmount + 0.005 || amount > capacity.availableAmount + 0.005) {
      throw new Error(t('bam.inv.freePostingNoMatchingAmount'));
    }
    const now = nowIso();
    payment = stripUndefinedDeep({
      id: paymentInput.id || createPaymentId(),
      documentType: 'supplier_invoice',
      documentId: expense.id,
      direction: 'outgoing',
      amount,
      date: paymentInput.date || todayIso(),
      settlementAccountCode: '2400',
      settlementKind: 'no_payment',
      paymentSource: 'supplier_invoice',
      reference: paymentInput.reference || expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
      note: paymentInput.note || 'Oppgjort uten betaling',
      createdByFlow: paymentInput.createdByFlow || 'manual-posting',
      manualPostingId: paymentInput.manualPostingId,
      manualPostingVoucherNumber: paymentInput.manualPostingVoucherNumber,
      status: 'active',
      createdAt: now,
    });
    const nextExpenses = [...expenses];
    nextExpense = stripUndefinedDeep({ ...expense, settledWithoutPaymentAt: amount >= summary.remainingAmount - 0.005 ? (expense.settledWithoutPaymentAt || now) : undefined, updatedAt: now });
    nextExpenses[index] = nextExpense;
    return {
      ...state,
      expenses: nextExpenses,
      payments: [payment, ...safeArray(state.payments).filter((entry) => entry.id !== payment.id)],
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return { expense: nextExpense, payment };
};

const settleSupplierInvoiceWithoutPayment = async (expenseOrId, paymentInput = {}) => {
  const expenseId = typeof expenseOrId === 'string' ? expenseOrId : expenseOrId?.id;
  if (!paymentInput.manualPostingId) throw new Error(t('bam.inv.settleNeedsFreePosting'));
  if (!expenseId) throw new Error(t('bam.inv.missingSupplierInvoiceSettle'));
  try {
    const result = await authorizedPost('purchaseMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'settle_without_payment',
      expenseId,
      expense: {
        ...paymentInput,
        id: paymentInput.id || createPaymentId(),
        date: paymentInput.date || todayIso(),
      },
    });
    if (result?.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return { expense: result.expense || null, payment: safeArray(result.payments)[0] || null };
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return settleSupplierInvoiceWithoutPaymentLocal(expenseId, paymentInput);
  }
};

const mutateSupplierOverpayment = async (action, expenseId, expense = {}) => {
  if (!expenseId) throw new Error(t('bam.inv.missingSupplierInvoiceOverpayment'));
  const result = await authorizedPost('purchaseMutationHttp', {
    businessId: runtime.activeBusinessId,
    action,
    expenseId,
    expense: { ...expense, date: expense.date || todayIso() },
  });
  if (result?.state) runtime.appState = mergeAppMutationState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result;
};

const refundSupplierOverpayment = (input = {}) =>
  mutateSupplierOverpayment('refund_supplier_overpayment', input.expenseId, { id: input.id || createPaymentId(), amount: input.amount, date: input.date, settlementAccountCode: input.settlementAccountCode, note: input.note });

const reverseSupplierOverpaymentRefund = ({ expenseId, paymentId } = {}) =>
  mutateSupplierOverpayment('reverse_supplier_overpayment_refund', expenseId, { paymentId });

const allocateSupplierOverpayment = (input = {}) =>
  mutateSupplierOverpayment('allocate_supplier_overpayment', input.expenseId, { id: input.id || createPaymentId(), sourceExpenseId: input.sourceExpenseId, amount: input.amount, date: input.date, note: input.note });

const reverseSupplierOverpaymentAllocation = ({ expenseId, settlementId } = {}) =>
  mutateSupplierOverpayment('reverse_supplier_overpayment_allocation', expenseId, { settlementId });

const settleManualPostingOpenItems = async ({ posting, selections = [], date } = {}) => {
  const selected = safeArray(selections).filter((selection) => selection?.documentType && selection?.documentId);
  if (!selected.length) return [];
  const postingRef = posting?.savedEntry || posting || {};
  if (!postingRef.id) throw new Error(t('bam.inv.missingFreePostingToLink'));
  const result = await authorizedPost('manualPostingMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'link_open_items',
    postingId: postingRef.id,
    selections: selected.map((selection) => ({
      documentType: selection.documentType,
      documentId: selection.documentId,
      amount: num(selection.amount ?? selection.suggestedAmount ?? selection.maxAmount),
    })),
  });
  if (result?.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
    window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  }
  return safeArray(result?.settlements);
};

// Eksekverer en godkjent register_payment-kontrakt gjennom den hash-bundne
// capability-kommandoen. Backend oppretter approval/audit og delegerer deretter
// til samme innbetalingsmutasjon som fakturalisten bruker.
const registerAssistantPayment = async (executionPayload = {}) => {
  const payload = executionPayload && typeof executionPayload === 'object' ? executionPayload : {};
  if (payload.action !== 'register_payment') throw new Error(t('svc.unknownPaymentAction'));
  if (payload.documentType === 'supplier_invoice') {
    // Plumme har ingen leverandorfaktura-innbetaling enda -> handoff til bilagslisten.
    throw new Error(t('svc.supplierPaymentInVoucherList'));
  }
  if (payload.documentType !== 'invoice') throw new Error(t('svc.missingInvoiceTypeForPayment'));
  if (!payload.documentId) throw new Error(t('svc.missingInvoiceForPayment2'));
  if (payload.settlementKind === 'no_payment') {
    throw new Error(t('bam.inv.settleMustLinkFreePosting'));
  }
  const amount = Number(payload.paymentAmount);
  if (!Number.isFinite(amount) || amount <= 0) throw new Error(t('svc.missingValidAmount'));
  const result = await authorizedPost('registerAssistantInvoicePaymentCommandHttp', {
    businessId: runtime.activeBusinessId,
    executionPayload: payload,
  });
  if (result?.payment) {
    runtime.appState = mergeState({
      ...runtime.appState,
      payments: [result.payment, ...safeArray(runtime.appState?.payments).filter((entry) => entry?.id !== result.payment.id)],
      invoices: result?.invoice
        ? safeArray(runtime.appState?.invoices).map((entry) => entry?.id === result.invoice.id ? result.invoice : entry)
        : runtime.appState?.invoices,
    });
    emitRuntime();
  }
  return { payment: result?.payment, commandId: result?.commandId, documentId: payload.documentId, paymentMode: payload.paymentMode || 'full', amount };
};
